import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Lock, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import './WorkPage.css';

const projectsData = [
    {
        id: "paoz-hcc",
        title: "PAOZ Highfield Community Platform",
        category: "Non-Profit & Community",
        type: "web",
        status: "live",
        statusLabel: "Live Site",
        url: "https://paozhcc.com/",
        description: "A modern digital platform for PAOZ HCC designed to streamline community communications, sermon distribution, and event organization.",
        metrics: "High engagement & digital reach",
        tech: ["React", "Node.js", "Cloud Hosting"],
        accent: "linear-gradient(135deg, rgba(0, 56, 189, 0.4) 0%, rgba(4, 15, 43, 0.8) 100%)"
    },
    {
        id: "asset-base-zim",
        title: "Asset Base Zimbabwe",
        category: "Finance & Asset Management",
        type: "web",
        status: "live",
        statusLabel: "Live Site",
        url: "https://assetbasezim.com/",
        description: "Corporate digital presence for Asset Base Zimbabwe highlighting property listings, asset management services, and consultation lead funnels.",
        metrics: "Authoritative brand & lead generation",
        tech: ["React", "Web Architecture", "SEO"],
        accent: "linear-gradient(135deg, rgba(16, 99, 181, 0.4) 0%, rgba(6, 21, 53, 0.8) 100%)"
    },
    {
        id: "employee-management",
        title: "Employee Management System (EMS)",
        category: "Enterprise Workforce Product",
        type: "enterprise",
        status: "private",
        statusLabel: "Enterprise Showcase",
        url: null,
        description: "Proprietary workforce management system consolidating employee records, biometrics attendance, leave workflows, and automated payroll.",
        metrics: "40%+ Admin overhead reduction",
        tech: ["React", "Node.js", "PostgreSQL", "Enterprise Security"],
        accent: "linear-gradient(135deg, rgba(239, 128, 1, 0.4) 0%, rgba(4, 15, 43, 0.8) 100%)"
    },
    {
        id: "kodebuild-platform",
        title: "KodeBuild Digital Platform",
        category: "Technology & Software Engineering",
        type: "web",
        status: "live",
        statusLabel: "Live Site",
        url: "https://kodebuildtech.com",
        description: "Official digital home of KodeBuild engineered with dark glassmorphism, fast client routing, and structured solution architectures.",
        metrics: "Sub-second speed & global presence",
        tech: ["React", "Framer Motion", "Vercel"],
        accent: "linear-gradient(135deg, rgba(0, 56, 189, 0.3) 0%, rgba(239, 128, 1, 0.3) 100%)"
    }
];

const WorkPage = () => {
    const [activeFilter, setActiveFilter] = useState('all');

    const filteredProjects = activeFilter === 'all'
        ? projectsData
        : projectsData.filter(p => p.type === activeFilter);

    return (
        <motion.div
            className="work-page-new"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>Selected Works &amp; Case Studies | Kodebuild</title>
                <meta name="description" content="Explore selected client projects and enterprise products engineered by Kodebuild, including PAOZ HCC, Asset Base Zimbabwe, and our custom Employee Management System." />
                <link rel="canonical" href="https://kodebuildtech.com/work" />
                {/* Open Graph */}
                <meta property="og:title" content="Selected Works & Case Studies | Kodebuild" />
                <meta property="og:description" content="Explore selected client projects and enterprise products engineered by Kodebuild — from non-profit platforms to enterprise workforce systems." />
                <meta property="og:url" content="https://kodebuildtech.com/work" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Selected Works & Case Studies | Kodebuild" />
                <meta name="twitter:description" content="Explore selected client projects and enterprise products engineered by Kodebuild." />
                <meta name="twitter:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
            </Helmet>

            {/* Hero Section */}
            <section className="work-hero-new">
                <div className="work-hero-glow" />
                <div className="container">
                    <span className="section-badge">Selected Portfolio</span>
                    <h1 className="work-hero-title">
                        Engineered for <span className="text-gradient">Performance</span>
                    </h1>
                    <p className="work-hero-subtitle">
                        A curated collection of digital products, enterprise systems, and live client platforms engineered by KodeBuild.
                    </p>

                    {/* Filter Pills */}
                    <div className="work-filter-bar">
                        <button
                            className={`filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('all')}
                        >
                            All Works ({projectsData.length})
                        </button>
                        <button
                            className={`filter-pill ${activeFilter === 'web' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('web')}
                        >
                            Live Platforms ({projectsData.filter(p => p.type === 'web').length})
                        </button>
                        <button
                            className={`filter-pill ${activeFilter === 'enterprise' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('enterprise')}
                        >
                            Enterprise Systems ({projectsData.filter(p => p.type === 'enterprise').length})
                        </button>
                    </div>
                </div>
            </section>

            {/* Portfolio Grid */}
            <section className="work-grid-section">
                <div className="container">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeFilter}
                            className="portfolio-cards-grid"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.3 }}
                        >
                            {filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    className="portfolio-card glass"
                                    initial={{ opacity: 0, y: 25 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.08 }}
                                >
                                    {/* Top Card Header Banner */}
                                    <div className="card-banner-visual" style={{ background: project.accent }}>
                                        <div className="banner-top-tags">
                                            <span className="card-cat-badge">{project.category}</span>
                                            <span className={`card-status-badge ${project.status}`}>
                                                {project.status === 'live' ? (
                                                    <>
                                                        <span className="pulse-green-dot" />
                                                        {project.statusLabel}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lock size={12} style={{ marginRight: '4px' }} />
                                                        {project.statusLabel}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                        <h3 className="card-project-title">{project.title}</h3>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body-content">
                                        <p className="card-project-desc">{project.description}</p>
                                        
                                        <div className="card-impact-row">
                                            <Sparkles size={14} className="impact-star" />
                                            <span><strong>Key Result:</strong> {project.metrics}</span>
                                        </div>

                                        <div className="card-tech-row">
                                            {project.tech.map((t, idx) => (
                                                <span key={idx} className="tech-badge">{t}</span>
                                            ))}
                                        </div>

                                        {/* Action CTA */}
                                        <div className="card-action-bar">
                                            {project.status === 'live' ? (
                                                <a
                                                    href={project.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="card-action-btn live"
                                                >
                                                    <span>Visit Live Site</span>
                                                    <ExternalLink size={15} />
                                                </a>
                                            ) : (
                                                <Link
                                                    to="/contact"
                                                    className="card-action-btn private"
                                                >
                                                    <span>Request Enterprise Demo</span>
                                                    <ShieldCheck size={15} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="work-cta-new">
                <div className="container">
                    <div className="cta-box-glass glass">
                        <h2>Ready to engineer your custom software?</h2>
                        <p>Whether you need a high-converting web platform or an enterprise solution, we turn your requirements into high-performance digital reality.</p>
                        <Link to="/contact" className="btn-primary glow-hover">
                            Start Your Project <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                        </Link>
                    </div>
                </div>
            </section>
        </motion.div>
    );
};

export default WorkPage;
